import FormWatcher from '@form/FormWatcher';
import { Form, FormInput } from '@form/index';
import Joi from 'joi';
import { omit } from 'lodash';
import React, { FC, useCallback, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SWRConfiguration } from 'swr';
import { useApi } from '../../../hooks/useApi';
import { useClubSlug } from '../../Clubs/hooks';
import Loader from '../../Loader';
import { useInvoice, useIssuerSetting, usePaymentTerms } from '../data';
import { InvoiceItemsFields, InvoiceNumberInput, InvoicePriceComponent } from '../InvoiceForm';
import { InvoiceRecipientNameField } from '../InvoiceForm/InvoiceRecipientNameField';
import { UserInvoice } from '../types';

interface Props {
    invoice: Required<Pick<UserInvoice, 'id'>> & Partial<UserInvoice>;
    callback?: (newCredit: UserInvoice) => void;
}

const InvoiceCreditForm: FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
    invoice: _invoice,
    callback
}) => {
    const { t } = useTranslation('invoice');
    const clubSlug = useClubSlug();
    const api = useApi();
    const [currentIssuer, setCurrentIssuer] = useState(_invoice?.issuer);
    const [isLoading, setIsLoading] = useState(false);

    const swrConfig: SWRConfiguration = {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateOnMount: true
    };

    const { data: paymentTermData, isLoading: paymentTermIsLoading } = usePaymentTerms(clubSlug);
    const {
        invoice,
        isLoading: isLoadingInvoice,
        mutate: mutateInvoice
    } = useInvoice(_invoice?.id, {
        ...swrConfig,
        onSuccess(data) {
            if (!currentIssuer) setCurrentIssuer(data?.data.issuer);
        }
    });
    const { issuerSetting, isLoading: issuerSettingIsLoading } = useIssuerSetting(clubSlug, currentIssuer, swrConfig);
    const isLoadingData = issuerSettingIsLoading || isLoadingInvoice || paymentTermIsLoading;

    const handleSubmit = useCallback(
        (data: FieldValues, event: any) => {
            setIsLoading(true);
            return api({
                url: 'invoice/' + clubSlug,
                method: 'POST',
                data: omit(
                    {
                        ...data,
                        items: data.items.map((item) => omit(item, 'id')),
                        is_credit: true,
                        user_invoice_setting_id: issuerSetting?.id,
                        credited_invoice_id: _invoice.id,
                        parent_category_id: _invoice.category_id ?? null
                    },
                    [
                        'number',
                        'id',
                        'created_at',
                        'updated_at',
                        'last_datev_export',
                        'finalized_at',
                        'send_at',
                        'payed_at'
                    ]
                ),
                onFailure: () => setIsLoading(false),
                onSuccess: ({ data }) => {
                    if (callback && event.nativeEvent.submitter.id !== 'invoice_presubmit') {
                        callback(data);
                    } else {
                        void mutateInvoice(data);
                    }
                    setIsLoading(false);
                }
            });
        },
        [callback, issuerSetting, invoice]
    );

    const schema = Joi.object({
        title: Joi.string().max(255).allow('', null).label(t('invoiceForm.fields.title')),
        header_title: Joi.string().max(255).allow('', null).label(t('invoiceForm.fields.creditHeaderTitle')),
        header_entry: Joi.string().allow('', null).label(t('invoiceForm.fields.headerEntry')),
        line0: Joi.string().max(255).required().label(t('invoiceForm.fields.name')),
        line1: Joi.string().max(255).allow('', null).label(t('invoiceForm.fields.addressLine2')),
        line2: Joi.string().max(255).allow('', null).label(t('invoiceForm.fields.addressLine3')),
        city: Joi.string().max(255).allow('', null).label(t('invoiceForm.fields.city')),
        postcode: Joi.string().max(255).allow('', null).label(t('invoiceForm.fields.postcode')),
        date: Joi.date().required().label(t('invoiceForm.fields.thisField')),
        due_date: Joi.date().allow(null, '').label(t('invoiceForm.fields.dueDate')),
        iban: Joi.string().allow('', null).label(t('invoiceForm.fields.iban')),
        bic: Joi.string().allow('', null).label(t('invoiceForm.fields.bic')),
        debtor: Joi.string().allow('', null).label(t('invoiceForm.fields.debtorNumber')),
        items: Joi.array()
            .items(
                Joi.object({
                    description: Joi.string().required().label(t('invoiceForm.fields.description')),
                    description_long: Joi.string().allow('', null).label(t('invoiceForm.fields.descriptionLong')),
                    amount: Joi.number().min(0).max(10000).required().label(t('invoiceForm.fields.amount')),
                    taxes: Joi.number().allow(null, '').min(0).max(100).label(t('invoiceForm.fields.taxes')),
                    price_per_item: Joi.number().min(0).required().label(t('invoiceForm.fields.pricePerItem'))
                }).unknown()
            )
            .min(1)
            .max(100)
            .required()
            .label(t('invoiceForm.fields.items')),
        credits: Joi.array()
            .items(
                Joi.alternatives().conditional('percentage', {
                    then: Joi.object({
                        description: Joi.string().required().max(255).label(t('invoiceForm.fields.description')),
                        amount: Joi.number().min(0).max(100).required().label(t('invoiceForm.fields.amount'))
                    }).unknown(),
                    otherwise: Joi.object({
                        description: Joi.string().required().max(255).label(t('invoiceForm.fields.description')),
                        amount: Joi.number().min(0).max(100000).required().label(t('invoiceForm.fields.amount'))
                    }).unknown()
                })
            )
            .max(100)
            .label(t('invoiceForm.fields.credits')),
        taxes: Joi.number().min(0).required().label(t('invoiceForm.fields.taxesShort')),
        remarks: Joi.string().max(5000).allow('', null),
        email: Joi.string()
            .email({ tlds: { allow: false } })
            .allow('', null)
            .max(255)
            .label(t('invoiceForm.fields.recipientEmail'))
    }).unknown();

    const formInputProps: {
        withoutShifting: boolean;
        size: 'sm';
    } = {
        withoutShifting: true,
        size: 'sm'
    };

    return (
        <Loader isLoading={isLoadingData}>
            <Form
                onSubmit={handleSubmit}
                validationSchema={schema}
                setDefaultValuesOnRerender={false}
                defaultValues={{
                    date: new Date(),
                    taxes: 0,
                    ...(invoice || {}),
                    credits: undefined
                }}
            >
                <div className="tw-flex tw-gap-6">
                    <div className="tw-flex-1">
                        <div className="tw-grid tw-grid-cols-12 tw-gap-4">
                            <div className="tw-col-span-12 md:tw-col-span-5">
                                <FormInput placeholder={t('invoiceForm.fields.customerNumber')} {...formInputProps} name="party_id" />
                                <InvoiceRecipientNameField placeholder={t('invoiceForm.fields.name')} {...formInputProps} name="line0" />
                                <FormInput {...formInputProps} placeholder={t('invoiceForm.fields.streetAndNumber')} name="line1" />
                                <FormInput {...formInputProps} placeholder={t('invoiceForm.fields.addressLine2')} name="line2" />
                                <FormInput {...formInputProps} placeholder={t('invoiceForm.fields.cityExample')} name="city" />
                                <FormInput {...formInputProps} placeholder={t('invoiceForm.fields.postcode')} name="postcode" />
                            </div>
                            <div className="tw-col-span-0 md:tw-col-span-2" />
                            <div className="tw-col-span-12 md:tw-col-span-5">
                                <FormInput
                                    name="title"
                                    placeholder={t('invoiceForm.fields.internalTitle')}
                                    helperText={t('invoiceForm.fields.internalTitleHelper')}
                                />
                                <FormInput {...formInputProps} placeholder={t('invoiceForm.fields.debtorNumber')} name="debtor" />
                            </div>
                            <div className="tw-col-span-12 md:tw-col-span-5">
                                <FormInput
                                    {...formInputProps}
                                    name="email"
                                    placeholder={t('invoiceForm.fields.recipientEmail')}
                                />
                                {!_invoice?.number && (
                                    <InvoiceNumberInput
                                        {...formInputProps}
                                        issuer={currentIssuer}
                                        clubSlug={isLoading ? null : clubSlug}
                                        name="number"
                                        manual={invoice !== undefined}
                                        label={t('invoiceForm.fields.creditNumber')}
                                        isCredit={true}
                                    />
                                )}
                                <FormInput {...formInputProps} name="header_title" placeholder={t('invoiceForm.fields.creditHeaderTitle')} />
                            </div>
                            <div className="tw-col-span-0 md:tw-col-span-2" />
                            <div className="tw-col-span-12 md:tw-col-span-5">
                                <FormInput.DatePicker
                                    {...formInputProps}
                                    placeholder={t('invoiceForm.fields.creditDate')}
                                    name="date"
                                />
                                <FormInput.Select
                                    disableCustomValue
                                    name="payment_term_id"
                                    options={[
                                        {
                                            label: t('invoiceForm.fields.customPaymentTerm'),
                                            key: ''
                                        },
                                        ...(paymentTermData?.data?.map((pt) => ({
                                            label: `${pt.key} - ${pt.title}`,
                                            key: pt.id
                                        })) ?? [])
                                    ]}
                                />
                                <FormWatcher name="payment_term_id">
                                    {(payment_term) =>
                                        !payment_term ? (
                                            <FormInput.DatePicker
                                                {...formInputProps}
                                                placeholder={t('invoiceForm.fields.dueDate')}
                                                name="due_date"
                                            />
                                        ) : null
                                    }
                                </FormWatcher>

                                <FormInput.DatePicker
                                    {...formInputProps}
                                    placeholder={t('invoiceForm.fields.deliveryDate')}
                                    name="delivery_date"
                                />
                            </div>
                            <div className="tw-col-span-12">
                                <FormInput {...formInputProps} name="header_entry" placeholder={t('invoiceForm.fields.headerEntry')} />
                                <InvoiceItemsFields name="items" />
                            </div>

                            {/* <Grid xs={12} sm={6} md={8} /> */}
                            <div className="tw-col-span-12 sm:tw-col-span-6 md:tw-col-span-8" />
                            <div className="tw-col-span-12 sm:tw-col-span-6 md:tw-col-span-4">
                                <FormInput
                                    type="number"
                                    min={0}
                                    max={100}
                                    name="taxes"
                                    placeholder={t('invoiceForm.fields.vat')}
                                    labelRight="%"
                                />
                                <InvoicePriceComponent invoice={invoice} setting={issuerSetting} isCredit={true} />
                            </div>
                            <div className="tw-col-span-12">
                                <FormInput.WYSIWYG label={t('invoiceForm.fields.remarks')} name="remarks" />
                            </div>
                        </div>
                        <FormInput.Submit isLoading={isLoading}>{t('invoiceCredit.saveAndClose')}</FormInput.Submit>
                    </div>
                </div>
            </Form>
        </Loader>
    );
};

export default InvoiceCreditForm;
